/**
 * String 추가·복사 시 대상 Array seq 결정
 *
 * 우선순위 (맵 중심 — 트리 미선택 Ctrl+드래그 복사 대응):
 * 1) 놓는 좌표에 겹친 String/Module의 Array
 * 2) 놓는 좌표 근처(픽셀 반경) 가장 가까운 String/Module의 Array
 * 3) 원본 String의 Array (드래그 복사·붙여넣기)
 * 4) 트리 포커스 Array
 */
import {
  panelPointerMovePerfBeginHandler,
  panelPointerMovePerfEndHandler
} from './panelPointerMovePerfLog.js';

/** @param {import('ol/Feature').default} feature */
export function arraySeqFromOlFeature(feature) {
  if (!feature?.get) return null;
  const seq = feature.get('solarArraySeq') ?? feature.get('solarArrayId');
  return seq != null && seq !== '' ? Number(seq) : null;
}

const NEAREST_PIXEL_RADIUS = 100;

/**
 * 픽셀 위치의 String/Module → Array seq (Ctrl+드래그 클론 제외)
 */
export function pickArraySeqFromFeaturesAtPixel(map, pixel, vectorLayer) {
  if (!map || !pixel || !vectorLayer) return null;

  const perf = panelPointerMovePerfBeginHandler('placement-at-pixel');
  const features =
    map.getFeaturesAtPixel(pixel, {
      layerFilter: layer => layer === vectorLayer
    }) || [];
  panelPointerMovePerfEndHandler(perf, { hitCount: features.length });

  for (const feature of features) {
    if (feature.get?.('isCtrlDragClone')) continue;
    const seq = arraySeqFromOlFeature(feature);
    if (seq != null) return seq;
  }
  return null;
}

/**
 * 빈 공간에 놓았을 때 — 근처 String/Module 중심으로 Array 추론
 */
export function resolveNearestArraySeqAtCoordinate(map, vectorSource, coordinate) {
  if (!map || !vectorSource || !coordinate) return null;

  const dropPixel = map.getPixelFromCoordinate(coordinate);
  if (!dropPixel) return null;

  const resolution = map.getView?.()?.getResolution?.() ?? 1;
  const mapBuffer = NEAREST_PIXEL_RADIUS * resolution;
  const searchExtent = [
    coordinate[0] - mapBuffer,
    coordinate[1] - mapBuffer,
    coordinate[0] + mapBuffer,
    coordinate[1] + mapBuffer
  ];

  let bestSeq = null;
  let bestDistSq = NEAREST_PIXEL_RADIUS * NEAREST_PIXEL_RADIUS;
  let scanned = 0;

  const perf = panelPointerMovePerfBeginHandler('placement-nearest-scan');
  const candidates =
    typeof vectorSource.getFeaturesInExtent === 'function'
      ? vectorSource.getFeaturesInExtent(searchExtent)
      : vectorSource.getFeatures?.() ?? [];
  for (const feature of candidates) {
    scanned += 1;
    if (feature.get?.('isCtrlDragClone')) continue;

    const featureType = (feature.get('featureType') || '').toLowerCase();
    if (featureType !== 'string' && featureType !== 'module') continue;

    const geometry = feature.getGeometry();
    if (!geometry) continue;

    const extent = geometry.getExtent();
    const center = [(extent[0] + extent[2]) / 2, (extent[1] + extent[3]) / 2];
    const featurePixel = map.getPixelFromCoordinate(center);
    if (!featurePixel) continue;

    const dx = featurePixel[0] - dropPixel[0];
    const dy = featurePixel[1] - dropPixel[1];
    const distSq = dx * dx + dy * dy;
    if (distSq < bestDistSq) {
      bestDistSq = distSq;
      bestSeq = arraySeqFromOlFeature(feature);
    }
  }
  panelPointerMovePerfEndHandler(perf, { scanned, found: bestSeq != null });

  return bestSeq;
}

/**
 * @param {object} params
 * @param {import('ol/Map').default} [params.map]
 * @param {import('ol/layer/Vector').default} [params.vectorLayer]
 * @param {import('ol/source/Vector').default} [params.vectorSource]
 * @param {[number, number]} [params.dropCoordinate]
 * @param {number|null} [params.sourceArraySeq]
 * @param {number|null} [params.treeArraySeq]
 */
export function resolveStringPlacementArraySeq({
  map,
  vectorLayer,
  vectorSource,
  dropCoordinate,
  sourceArraySeq = null,
  treeArraySeq = null
}) {
  if (dropCoordinate && map && vectorLayer) {
    const pixel = map.getPixelFromCoordinate(dropCoordinate);
    const atDrop = pickArraySeqFromFeaturesAtPixel(map, pixel, vectorLayer);
    if (atDrop != null) return atDrop;

    const nearest = resolveNearestArraySeqAtCoordinate(map, vectorSource, dropCoordinate);
    if (nearest != null) return nearest;
  }

  if (sourceArraySeq != null && sourceArraySeq !== '') {
    return Number(sourceArraySeq);
  }
  if (treeArraySeq != null && treeArraySeq !== '') {
    return Number(treeArraySeq);
  }
  return null;
}
